// src/pages/Resumes.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Sparkles, Plus } from 'lucide-react';

interface Resume {
  id: number;
  title: string;
  content: string;
  target_role: string;
  version: number;
  created_at: string;
}

function Resumes() {
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<{ resumes: Resume[] }>({
    queryKey: ['resumes'],
    queryFn: async () => {
      const response = await fetch('/api/resumes');
      if (!response.ok) {
        throw new Error('Failed to fetch resumes');
      }
      return response.json();
    },
  });

  interface AnalyzeResponse {
    matchScore: number;
    keywords: string[];
    missingKeywords: string[];
    suggestions: string[];
    aiAnalysis?: string;
  }

  interface AnalyzeVariables {
    resume: string;
    jobDesc: string;
  }

  const createResumeMutation = useMutation({
    mutationFn: async (newResume: { title: string; content: string; targetRole?: string; skillsEmphasized?: string }) => {
      const response = await fetch('/api/resumes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newResume),
      });
      if (!response.ok) {
        const error = await response.json() as { error?: string };
        throw new Error(error.error || 'Failed to create resume');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] });
      setShowForm(false);
    },
  });

  const analyzeMutation = useMutation<AnalyzeResponse, Error, AnalyzeVariables>({
    mutationFn: async ({ resume, jobDesc }: AnalyzeVariables): Promise<AnalyzeResponse> => {
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resume, jobDescription: jobDesc }),
      });
      if (!response.ok) {
        const error = await response.json() as { error?: string };
        throw new Error(error.error || 'Failed to analyze resume');
      }
      const data = await response.json() as AnalyzeResponse;
      // Ensure matchScore is a valid number
      if (typeof data.matchScore !== 'number' || isNaN(data.matchScore)) {
        data.matchScore = 0;
      }
      data.matchScore = Math.max(0, Math.min(100, data.matchScore));
      return data;
    },
  });

  const resumes: Resume[] = data?.resumes || [];

  const handleAnalyze = () => {
    if (selectedResume && jobDescription) {
      analyzeMutation.mutate({
        resume: selectedResume.content,
        jobDesc: jobDescription,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Resumes</h1>
          <p className="mt-2 text-gray-600">Manage and optimize your resume versions</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Resume
        </button>
      </div>

      {showForm && (
        <ResumeForm
          onSubmit={(data) => createResumeMutation.mutate(data)}
          onCancel={() => setShowForm(false)}
          isLoading={createResumeMutation.isPending}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resume List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Your Resumes</h2>
          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading resumes...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">
                  {error instanceof Error ? error.message : 'Failed to load resumes'}
                </p>
              </div>
            </div>
          ) : resumes.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No resumes yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {resumes.map((resume) => (
                <div
                  key={resume.id}
                  onClick={() => setSelectedResume(resume)}
                  className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all ${
                    selectedResume?.id === resume.id
                      ? 'ring-2 ring-primary-500'
                      : 'hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{resume.title}</h3>
                      <p className="mt-1 text-xs text-gray-500">{resume.target_role}</p>
                      <p className="mt-1 text-xs text-gray-400">
                        v{resume.version} • {new Date(resume.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Analysis */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">AI Analysis</h2>
            <Sparkles className="h-5 w-5 text-primary-600" />
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            {!selectedResume ? (
              <p className="text-sm text-gray-500 text-center py-8">
                Select a resume to analyze
              </p>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description
                  </label>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    rows={8}
                    placeholder="Paste the job description here..."
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                
                <button
                  onClick={handleAnalyze}
                  disabled={!jobDescription || analyzeMutation.isPending}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {analyzeMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Analyze with AI
                    </>
                  )}
                </button>

                {analyzeMutation.isError && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">
                      {analyzeMutation.error?.message || 'Failed to analyze resume. Please try again.'}
                    </p>
                  </div>
                )}

                {analyzeMutation.data && (
                  <div className="mt-6 space-y-4 animate-fade-in">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Match Score</span>
                        <span className="text-2xl font-bold text-primary-600">
                          {typeof analyzeMutation.data.matchScore === 'number' 
                            ? Math.round(analyzeMutation.data.matchScore) 
                            : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${typeof analyzeMutation.data.matchScore === 'number' 
                              ? Math.max(0, Math.min(100, Math.round(analyzeMutation.data.matchScore))) 
                              : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    {analyzeMutation.data.aiAnalysis && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">AI Analysis</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">
                          {analyzeMutation.data.aiAnalysis}
                        </p>
                      </div>
                    )}

                    {analyzeMutation.data.keywords?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Matched Keywords</h4>
                        <div className="flex flex-wrap gap-2">
                          {analyzeMutation.data.keywords.map((keyword: string, i: number) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {analyzeMutation.data.missingKeywords?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Missing Keywords</h4>
                        <div className="flex flex-wrap gap-2">
                          {analyzeMutation.data.missingKeywords.map((keyword: string, i: number) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {analyzeMutation.data.suggestions?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Suggestions</h4>
                        <ul className="space-y-2">
                          {analyzeMutation.data.suggestions.map((suggestion: string, i: number) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start">
                              <span className="text-primary-600 mr-2">•</span>
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResumeForm({ 
  onSubmit, 
  onCancel,
  isLoading
}: { 
  onSubmit: (data: { title: string; content: string; targetRole?: string; skillsEmphasized?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetRole: '',
    skillsEmphasized: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      return;
    }
    onSubmit({
      title: formData.title,
      content: formData.content,
      targetRole: formData.targetRole || undefined,
      skillsEmphasized: formData.skillsEmphasized || undefined,
    });
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Resume</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Software Engineer Resume"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Content *</label>
          <textarea
            required
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            rows={12}
            placeholder="Paste your resume content here..."
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Target Role</label>
            <input
              type="text"
              value={formData.targetRole}
              onChange={(e) => setFormData({ ...formData, targetRole: e.target.value })}
              placeholder="e.g., Senior Software Engineer"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Skills Emphasized</label>
            <input
              type="text"
              value={formData.skillsEmphasized}
              onChange={(e) => setFormData({ ...formData, skillsEmphasized: e.target.value })}
              placeholder="e.g., React, TypeScript, AWS"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.title.trim() || !formData.content.trim()}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating...' : 'Create Resume'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Resumes;
